import { StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 "Roadmap & Scope" tab (formerly "Support Rails").
 * Mirrors the as-built Diagnosis/Metrics/Governance/Closeout view-switcher: chips set an active view;
 * inactive views stay mounted via `hidden`; an Expand all / Collapse toggle (sibling OUTSIDE the
 * tablist, `aria-pressed`) reveals every view. Reframes the 9 "what's built now vs. roadmap" cards as a
 * transparency surface grouped by seed-authored maturity tier, with cross-links to where each
 * capability is actually proven. Internal `shells` key + `shell-*` testids are preserved.
 */

export const ROADMAP_SECTIONS = [
  { key: 'summary', label: 'Summary' },
  { key: 'live', label: 'Live now' },
  { key: 'roadmap', label: 'Roadmap' },
];

// Internal shell keys → human labels (moved here from RapidPages; keys are unchanged per CLAUDE.md).
export const SHELL_LABELS = {
  ArtifactCenter: 'Artifact Center',
  Retrospective: 'Retrospective',
  GovernanceEditor: 'Governance Brief Builder',
  RelatedEffortReview: 'Related-Effort / Policy Review',
  Pod3: 'Pod 3',
  CostCalculator: 'Cost-Avoidance Calculator',
  MOPMOELibrary: 'MOP/MOE Library',
  RBACMatrix: 'Role / RBAC Matrix',
  ExportPackage: 'Export Package',
};

const TIER_META = {
  Live: { label: 'Live now', group: 'live', tone: 'done' },
  P1: { label: 'Roadmap · P1', group: 'roadmap', tone: 'amber' },
  P2: { label: 'Roadmap · P2', group: 'roadmap', tone: 'amber' },
  Future: { label: 'Future capability', group: 'roadmap', tone: 'open' },
  Mock: { label: 'Roadmap · planned', group: 'roadmap', tone: 'open' },
};

// Cross-links from a "live" capability to the surface that actually proves it.
const CROSS_LINKS = {
  GovernanceEditor: { tab: 'governance', label: 'Open Governance' },
  ArtifactCenter: { tab: 'canvas', label: 'Open Scope Canvas' },
  Retrospective: { tab: 'closeout', label: 'Open Closeout' },
  MOPMOELibrary: { tab: 'metrics', label: 'Open Metrics library' },
  CostCalculator: { tab: 'metrics', label: 'Open Metrics' },
};

function slug(value = '') {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Shape the seeded shells + structured content into tier-aware items (presentation-ready).
export function deriveShellTiers(shells = {}, structured = {}) {
  return Object.entries(shells).map(([key, description]) => {
    const detail = structured[key] || {};
    const tier = detail.tier && TIER_META[detail.tier] ? detail.tier : 'Live';
    return { key, label: SHELL_LABELS[key] || key, description, detail, tier };
  });
}

export function summarizeTiers(items = []) {
  const counts = { Live: 0, P1: 0, P2: 0, Future: 0, Mock: 0 };
  items.forEach((item) => {
    counts[item.tier] = (counts[item.tier] || 0) + 1;
  });
  const live = counts.Live;
  const roadmap = items.length - live;
  return { counts, live, roadmap, total: items.length };
}

// ---------------------------------------------------------------------------
// View-switcher nav (Expand all OUTSIDE the tablist)
// ---------------------------------------------------------------------------

export function RoadmapSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="roadmap-section-nav" data-testid="roadmap-section-nav">
      <div className="roadmap-nav-tabs" role="tablist" aria-label="Roadmap and scope sections">
        {ROADMAP_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`roadmap-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`roadmap-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`roadmap-nav-chip roadmap-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="roadmap-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'summary' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Always-on Roadmap Summary
// ---------------------------------------------------------------------------

export function RoadmapSummaryPanel({ tiers }) {
  const { counts, live, roadmap, total } = tiers;
  return (
    <section className="rapid-panel roadmap-summary-panel" data-testid="roadmap-summary-panel">
      <div className="roadmap-summary-head">
        <div className="eyebrow">Roadmap &amp; scope</div>
        <h3 className="roadmap-summary-headline">
          What&rsquo;s built now vs. the credible roadmap
        </h3>
      </div>

      <div className="roadmap-summary-stats">
        <div className="roadmap-summary-stat is-primary">
          <span className="k">Live now</span>
          <span className="v" data-testid="roadmap-summary-live-count">
            {live}
          </span>
          <span className="sub">usable today</span>
        </div>
        <div className="roadmap-summary-stat">
          <span className="k">On the roadmap</span>
          <span className="v" data-testid="roadmap-summary-roadmap-count">
            {roadmap}
          </span>
          <span className="sub">planned</span>
        </div>
        <div className="roadmap-summary-stat">
          <span className="k">Capabilities mapped</span>
          <span className="v" data-testid="roadmap-summary-total-count">
            {total}
          </span>
          <span className="sub">full transparency</span>
        </div>
        <div className="roadmap-summary-stat is-text-stat">
          <span className="k">Tier breakdown</span>
          <span className="roadmap-tier-chips">
            {Object.entries(counts)
              .filter(([, n]) => n > 0)
              .map(([tier, n]) => (
                <span key={tier} className="chip outline sm">
                  {TIER_META[tier].label}: {n}
                </span>
              ))}
          </span>
        </div>
      </div>

      <div className="roadmap-summary-notes">
        <div className="roadmap-summary-note is-trust">
          <h4>What you can trust today</h4>
          <p>
            {live} capabilities are usable today — diagnosis, metrics, governance brief, closeout,
            and the linked artifacts behind them.
          </p>
        </div>
        <div className="roadmap-summary-note is-roadmap">
          <h4>What&rsquo;s roadmap</h4>
          <p>
            The remaining {roadmap} are labeled by tier (P1 / P2 / future) — nothing here is a dead
            end; each is an honest, planned next step.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shell card (preserves shell-{slug} testid + all structured sub-sections)
// ---------------------------------------------------------------------------

function DetailList({ items, className = 'rapid-record-list' }) {
  if (!items?.length) return null;
  return (
    <ul className={className}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function ShellCard({ item, onOpenTab }) {
  const { key, label, description, detail, tier } = item;
  const meta = TIER_META[tier] || TIER_META.Live;
  const crossLink = CROSS_LINKS[key];
  return (
    <section className="rapid-panel roadmap-shell-card" data-testid={`shell-${slug(label)}`}>
      <div className="roadmap-shell-head">
        <StatusChip status={meta.tone}>{meta.label}</StatusChip>
        <h3>{label}</h3>
      </div>
      <p>{description}</p>

      {detail.headline ? <div className="eyebrow">{detail.headline}</div> : null}
      <DetailList items={detail.contents} />
      {detail.recipients ? (
        <div className="muted small">Recipients: {detail.recipients.join(', ')}</div>
      ) : null}
      <DetailList items={detail.examples} />
      <DetailList items={detail.notes} className="rapid-inline-list" />
      {detail.assumptions ? (
        <>
          <div className="eyebrow">Assumptions</div>
          <DetailList items={detail.assumptions} />
        </>
      ) : null}
      {detail.sensitivityNotes ? (
        <>
          <div className="eyebrow">Sensitivity notes</div>
          <DetailList items={detail.sensitivityNotes} className="rapid-inline-list" />
        </>
      ) : null}
      <DetailList items={detail.relatedEfforts} />
      <DetailList items={detail.policyChecks} className="rapid-inline-list" />
      <DetailList items={detail.lessons} />
      {detail.followOnActions ? (
        <>
          <div className="eyebrow">Follow-on actions</div>
          <DetailList items={detail.followOnActions} className="rapid-inline-list" />
        </>
      ) : null}

      {crossLink ? (
        <button
          type="button"
          className="btn ghost sm roadmap-shell-link"
          data-testid={`roadmap-open-${slug(label)}`}
          onClick={() => onOpenTab(crossLink.tab)}
        >
          {crossLink.label}
        </button>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export function RoadmapCatalogView({ testId, eyebrow, title, emptyText, items, onOpenTab }) {
  return (
    <section className="rapid-panel roadmap-catalog-view" data-testid={testId}>
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">{eyebrow}</div>
          <h3>{title}</h3>
        </div>
      </div>
      {items.length ? (
        <div className="rapid-grid three-up roadmap-card-grid">
          {items.map((item) => (
            <ShellCard key={item.key} item={item} onOpenTab={onOpenTab} />
          ))}
        </div>
      ) : (
        <div className="rapid-empty-inline">{emptyText}</div>
      )}
    </section>
  );
}

export function RoadmapSummaryView({ items }) {
  const live = items.filter((item) => item.tier === 'Live');
  const roadmap = items.filter((item) => item.tier !== 'Live');
  return (
    <section className="rapid-panel roadmap-summary-view" data-testid="roadmap-summary-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">At a glance</div>
          <h3>Every capability, labeled by maturity</h3>
        </div>
      </div>
      <div className="roadmap-glance-grid">
        <div className="roadmap-glance-col">
          <h4>Live now ({live.length})</h4>
          <ul className="roadmap-glance-list">
            {live.map((item) => (
              <li key={item.key}>
                <span className="roadmap-glance-name">{item.label}</span>
                <StatusChip status="done">{TIER_META[item.tier].label}</StatusChip>
              </li>
            ))}
          </ul>
        </div>
        <div className="roadmap-glance-col">
          <h4>On the roadmap ({roadmap.length})</h4>
          <ul className="roadmap-glance-list">
            {roadmap.map((item) => (
              <li key={item.key}>
                <span className="roadmap-glance-name">{item.label}</span>
                <StatusChip status={TIER_META[item.tier].tone}>
                  {TIER_META[item.tier].label}
                </StatusChip>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
